import type { LexicalText, NoteTemplate, NoteTemplateInput } from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export function fetchCompanyNoteTemplates(context: ApiContext, options?: { scope?: "company" | "book" }) {
  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch<{ note_templates: NoteTemplate[] }>(context, `note_templates${params}`)
}

export async function createCompanyNoteTemplate(
  context: ApiContext,
  noteTemplate: NoteTemplateInput,
  options?: { scope?: "company" | "book" },
  newFiles: File[] = []
) {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const newSignedIds = await Promise.all(newFiles.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
  const files = [...(noteTemplate.files ?? []), ...newSignedIds]

  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch<{ note_template: NoteTemplate }>(context, `note_templates${params}`, {
    method: "POST",
    body: JSON.stringify({ note_template: { ...noteTemplate, files } }),
  })
}

export async function updateCompanyNoteTemplate(
  context: ApiContext,
  id: string,
  noteTemplate: Partial<NoteTemplateInput>,
  newFiles: File[] = []
) {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const newSignedIds = await Promise.all(newFiles.map((file) => uploadFileDirectly(file, apiRoot, context.token)))

  // Only send `files` when attachment data was supplied, so partial updates don't detach existing files.
  const files =
    noteTemplate.files !== undefined || newSignedIds.length > 0
      ? [...(noteTemplate.files ?? []), ...newSignedIds]
      : undefined
  const body = files !== undefined ? { ...noteTemplate, files } : noteTemplate

  return apiContextFetch<{ note_template: NoteTemplate }>(context, `note_templates/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ note_template: body }),
  })
}

export function destroyCompanyNoteTemplate(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, `note_templates/${id}`, {
    method: "DELETE",
  })
}

export function cloneCompanyNoteTemplate(context: ApiContext, id: string, bookId?: string) {
  return apiContextFetch<{ note_template: NoteTemplate }>(context, `note_templates/${id}/clone`, {
    method: "POST",
    body: JSON.stringify({ clone: { ...(bookId && { book_id: bookId }) } }),
  })
}

export function renderNoteTemplate(
  context: ApiContext,
  noteTemplateId: string,
  householdId?: string,
  meetingId?: string
) {
  return apiContextFetch<{ note_template_render: { content: LexicalText } }>(context, `note_template_renders`, {
    method: "POST",
    body: JSON.stringify({
      note_template_id: noteTemplateId,
      ...(householdId && { household_id: householdId }),
      ...(meetingId && { meeting_id: meetingId }),
    }),
  })
}
